import { useRef, useState } from 'react';
import { operatorDetails } from '../data/legalPages';
import { submitNewsletterLead } from '../lib/commerceApi';
import { mailtoFallbackMessage } from '../lib/leadCaptureGate';
import { trackEvent } from '../lib/analytics';
import { createLeadIdempotencyKey } from '../lib/leadCaptureGate';

export function NewsletterBlock() {
  const [message, setMessage] = useState('Vul je e-mailadres in en kies waar je als eerste bericht over wilt krijgen.');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formStartTracked = useRef(false);
  const submissionIdempotencyKey = useRef<string | null>(null);

  function trackFormStartOnce() {
    if (formStartTracked.current) return;
    formStartTracked.current = true;
    trackEvent({ event: 'form_start', location: 'newsletter', label: 'prelaunch_waitlist', routeFamily: 'lead_capture' });
    trackEvent({ event: 'lead_form_start', location: 'newsletter', label: 'prelaunch_waitlist', routeFamily: 'lead_capture' });
  }

  return (
    <section className="newsletter" id="nieuwsbrief" aria-label="Beschikbaarheidsupdate">
      <div>
        <p className="eyebrow">Blijf op de hoogte</p>
        <h2>Ontvang een update zodra de eerste producten bestelbaar zijn.</h2>
        <p>Kies waar je vooral naar zoekt. We gebruiken je bericht om de collectie en pakketadviezen beter af te stemmen op NL/BE spelers.</p>
        <ul className="leadTrust" aria-label="Wat je kunt verwachten">
          <li>Alleen een bericht zodra iets bestelbaar is, geen spam.</li>
          <li>Nederlandstalige service voor NL en Belgie.</li>
          <li>Geen fake voorraad of prijstrucs; kopen kan pas als het klopt.</li>
          <li>Uitschrijven kan altijd; dit is een prelaunch-aanmelding, geen bestelling.</li>
        </ul>
      </div>
      <form
        className="signup"
        noValidate={false}
        onFocus={trackFormStartOnce}
        onSubmit={async (event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          const email = String(data.get('email') ?? '').trim();
          const interest = String(data.get('interest') ?? '').trim();
          const consent = data.get('consent') === 'on';
          if (!consent) {
            setMessage('Bevestig dat we je mogen mailen over beschikbaarheid en advies.');
            trackEvent({ event: 'form_blocked', location: 'newsletter', label: 'missing_consent', routeFamily: 'lead_capture' });
            return;
          }
          setIsSubmitting(true);
          const body = [
            'Beschikbaarheidsupdate aanvraag via ProPickleball.nl',
            '',
            `E-mailadres: ${email}`,
            `Interesse: ${interest}`,
            '',
            'Let op: deze aanvraag is prelaunch en geen bestelling.'
          ].join('\n');
          trackEvent({ event: 'form_submit_attempt', location: 'newsletter', label: 'prelaunch_waitlist', routeFamily: 'lead_capture' });
          try {
            submissionIdempotencyKey.current ??= createLeadIdempotencyKey();
            const receipt = await submitNewsletterLead({
              email,
              interest,
              consent: true,
              idempotencyKey: submissionIdempotencyKey.current,
              source: 'homepage_newsletter'
            });
            trackEvent({
              event: 'sign_up',
              eventId: receipt.analytics.eventId,
              tenantId: receipt.analytics.tenantId,
              location: 'newsletter',
              label: 'prelaunch_waitlist',
              routeFamily: 'lead_capture'
            });
            submissionIdempotencyKey.current = null;
            setMessage('Dank je. Je staat op de lijst voor beschikbaarheidsupdates.');
            event.currentTarget.reset();
          } catch (error) {
            window.location.href = `mailto:${operatorDetails.email}?subject=${encodeURIComponent('Beschikbaarheidsupdate ProPickleball.nl')}&body=${encodeURIComponent(body)}`;
            trackEvent({ event: 'cta_click', location: 'newsletter', label: 'prelaunch_waitlist_mailto_fallback', routeFamily: 'lead_capture' });
            setMessage(mailtoFallbackMessage(error, operatorDetails.email));
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        <label>
          E-mailadres
          <input
            name="email"
            type="email"
            placeholder="naam@example.com"
            autoComplete="email"
            inputMode="email"
            aria-describedby="newsletter-status"
            required
          />
        </label>
        <label>
          Ik zoek vooral (optioneel)
          <select name="interest" defaultValue="Starterset">
            <option>Starterset</option>
            <option>Betere paddle</option>
            <option>Ballen/netten</option>
            <option>Clubpakket</option>
          </select>
        </label>
        <label className="checkboxLine">
          <input name="consent" type="checkbox" required />
          Je mag mij mailen over beschikbaarheid, advies en mijn aanvraag.
        </label>
        <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Versturen...' : 'Ontvang beschikbaarheidsupdate'}</button>
        <p className="formNote" id="newsletter-status" role="status" aria-live="polite">{message}</p>
      </form>
    </section>
  );
}
